const validPoint = (value) => {
  if (value?.lat == null || value?.lng == null) return null;
  const lat = Number(value?.lat);
  const lng = Number(value?.lng);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

export function normalizeTemporalSegments(rows) {
  return (Array.isArray(rows) ? rows : []).flatMap((row, index) => {
    const entityType = String(row?.entityType || "").toLowerCase().replace(/s$/, "");
    const entityId = String(row?.entityId ?? "").trim();
    if (row?.startS == null || row?.endS == null || row.startS === "" || row.endS === "") return [];
    const startS = Number(row?.startS);
    const endS = Number(row?.endS);
    const magnitude = row?.magnitude == null || row.magnitude === "" ? null : Number(row.magnitude);
    if (!["link", "node"].includes(entityType) || !entityId || !Number.isFinite(startS)
      || !Number.isFinite(endS) || startS < 0 || endS < startS) return [];
    const path = Array.isArray(row.path) ? row.path.map(validPoint).filter(Boolean) : [];
    return [{
      id: String(row.id || `${entityType}-${entityId}-${index}`),
      entityType,
      entityId,
      startS,
      endS,
      magnitude: Number.isFinite(magnitude) ? magnitude : null,
      label: String(row.label || `${entityType === "link" ? "Link" : "Node"} ${entityId}`),
      path: path.length > 1 ? path : null,
      position: validPoint(row.position),
    }];
  }).sort((a, b) => a.startS - b.startS || a.endS - b.endS);
}

export function temporalBounds(segments) {
  if (!segments.length) return { min: 0, max: 1 };
  const { min, max } = segments.reduce((bounds, segment) => ({
    min: Math.min(bounds.min, segment.startS),
    max: Math.max(bounds.max, segment.endS),
  }), { min: Infinity, max: -Infinity });
  return { min, max: Math.max(min + 1, max) };
}

export function temporalSampleStep(segments) {
  const times = [...new Set(segments.flatMap(({ startS, endS }) => [startS, endS]))].sort((a, b) => a - b);
  if (times.length < 2) return 1;
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  const milliseconds = times.map((time) => Math.round(time * 1000));
  const intervals = milliseconds.slice(1).map((time, index) => time - milliseconds[index]);
  return intervals.reduce((step, interval) => gcd(step, interval), 0) / 1000 || 1;
}

export function temporalMagnitudeRanges(segments, colors, customBoundaries) {
  let minimum = Infinity;
  let maximum = -Infinity;
  for (const segment of segments) {
    const value = segment.magnitude;
    if (!Number.isFinite(value)) continue;
    if (value < minimum) minimum = value;
    if (value > maximum) maximum = value;
  }
  if (minimum === Infinity) return [];
  const low = minimum === maximum ? minimum - 0.5 : minimum;
  const high = minimum === maximum ? maximum + 0.5 : maximum;
  const defaults = colors.slice(0, -1).map((_, index) => low + ((high - low) * (index + 1)) / colors.length);
  const validCustom = customBoundaries?.length === defaults.length && customBoundaries.every((value, index) =>
    Number.isFinite(value) && value > (index ? customBoundaries[index - 1] : low) && value < high);
  const boundaries = validCustom ? customBoundaries : defaults;
  return colors.map((color, index) => ({
    min: index ? boundaries[index - 1] : low,
    max: index < boundaries.length ? boundaries[index] : high,
    color,
  }));
}

export function activeTemporalSegments(segments, time, maxTime) {
  return segments.filter((segment) => segment.startS <= time &&
    (time < segment.endS || (time === segment.endS && (segment.startS === segment.endS || time === maxTime))));
}
